import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Save, Settings, Link, Zap, Shield, Globe, RotateCcw, Plus, Trash2, Edit3, ExternalLink, FormInput, DollarSign, MessageSquare, Mail, Share2, BarChart3, Wrench, Eye, EyeOff, AlertCircle, CheckCircle } from 'lucide-react';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { useApiQuotas } from '@/hooks/useApiQuotas';
import { useToast } from '@/hooks/use-toast';
import AdminSiteConfigBugReportForm from '@/components/AdminSiteConfigBugReportForm';
import AdminSiteConfigPricingForm from '@/components/AdminSiteConfigPricingForm';
import AdminSiteConfigFeedbackForm from '@/components/AdminSiteConfigFeedbackForm';
import AdminSiteConfigContactForm from '@/components/AdminSiteConfigContactForm';
import AdminSiteConfigPricingSettings from '@/components/AdminSiteConfigPricingSettings';
import AdminSiteConfigContentSettings from '@/components/AdminSiteConfigContentSettings';
import AdminSiteConfigContactSettings from '@/components/AdminSiteConfigContactSettings';
import AdminSiteConfigSocialMediaSettings from '@/components/AdminSiteConfigSocialMediaSettings';
import AdminSiteConfigSEOSettings from '@/components/AdminSiteConfigSEOSettings';
import AdminSiteConfigSystemSettings from '@/components/AdminSiteConfigSystemSettings';
import AdminSiteConfigSecuritySettings from '@/components/AdminSiteConfigSecuritySettings';

const AdminSiteConfiguration = () => {
  const { toast } = useToast();
  const { 
    configurations, 
    loading: configLoading, 
    updateConfiguration,
    addConfiguration,
    deleteConfiguration,
    resetToDefault 
  } = useSiteConfig();
  
  const { 
    featureFlags, 
    loading: flagsLoading, 
    updateFeatureFlag 
  } = useFeatureFlags();
  
  const { 
    quotas, 
    loading: quotasLoading, 
    updateQuota 
  } = useApiQuotas();

  const [saving, setSaving] = useState(false);
  const [newConfigDialog, setNewConfigDialog] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [newConfig, setNewConfig] = useState<{
    key: string;
    value: string;
    display_name: string;
    description: string;
    category: 'forms_links' | 'image_generation' | 'user_management' | 'system_health' | 'ui_settings' | 'security';
    data_type: 'string' | 'number' | 'boolean' | 'json' | 'url';
    is_required: boolean;
    validation_rules: any;
  }>({
    key: '',
    value: '',
    display_name: '',
    description: '',
    category: 'forms_links',
    data_type: 'string',
    is_required: false,
    validation_rules: {}
  });

  // Default configurations to seed if they don't exist - properly typed
  const defaultConfigurations: Array<{
    key: string;
    display_name: string;
    description: string;
    category: 'forms_links' | 'image_generation' | 'user_management' | 'system_health' | 'ui_settings' | 'security';
    data_type: 'string' | 'number' | 'boolean' | 'json' | 'url';
    value: string;
    is_required: boolean;
  }> = [
    // Google Forms
    { key: 'google_form_pricing', display_name: 'Pricing Inquiry Form', description: 'Google Form URL for pricing inquiries', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'bug_report_form_url', display_name: 'Bug Report Form', description: 'Google Form URL for bug reports (used by Help Center)', category: 'forms_links', data_type: 'url', value: 'https://forms.gle/vwv1nkkQSvSj8ts49', is_required: false },
    { key: 'google_form_feedback', display_name: 'User Feedback Form', description: 'Google Form URL for user feedback', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'google_form_contact', display_name: 'Contact Form', description: 'Google Form URL for general contact', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'google_form_enterprise', display_name: 'Enterprise Demo Form', description: 'Google Form URL for enterprise demo requests', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'google_form_support', display_name: 'Support Ticket Form', description: 'Google Form URL for support tickets', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    
    // Pricing Controls
    { key: 'pricing_section_enabled', display_name: 'Enable Pricing Section', description: 'Show/hide the pricing section', category: 'ui_settings', data_type: 'boolean', value: 'true', is_required: false },
    { key: 'pricing_cta_text', display_name: 'Pricing CTA Text', description: 'Call to action text for pricing section', category: 'ui_settings', data_type: 'string', value: 'Get Started Today', is_required: false },
    { key: 'pricing_special_offer', display_name: 'Special Offer Text', description: 'Special offer banner text', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'pricing_currency', display_name: 'Pricing Currency', description: 'Currency symbol for pricing', category: 'ui_settings', data_type: 'string', value: '$', is_required: false },
    
    // Website Content
    { key: 'site_title', display_name: 'Site Title', description: 'Main website title', category: 'ui_settings', data_type: 'string', value: 'Background Generator', is_required: true },
    { key: 'site_tagline', display_name: 'Site Tagline', description: 'Website tagline/subtitle', category: 'ui_settings', data_type: 'string', value: 'Create stunning backgrounds with AI', is_required: false },
    { key: 'hero_title', display_name: 'Hero Section Title', description: 'Main hero section title', category: 'ui_settings', data_type: 'string', value: 'Generate Amazing Backgrounds', is_required: false },
    { key: 'hero_subtitle', display_name: 'Hero Section Subtitle', description: 'Hero section subtitle', category: 'ui_settings', data_type: 'string', value: 'AI-powered background generation for your creative projects', is_required: false },
    { key: 'hero_cta_text', display_name: 'Hero CTA Button Text', description: 'Text for hero call-to-action button', category: 'ui_settings', data_type: 'string', value: 'Start Creating', is_required: false },
    { key: 'announcement_banner', display_name: 'Announcement Banner', description: 'Site-wide announcement banner text', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'announcement_enabled', display_name: 'Enable Announcement', description: 'Show/hide announcement banner', category: 'ui_settings', data_type: 'boolean', value: 'false', is_required: false },
    
    // Contact & Support
    { key: 'support_email', display_name: 'Support Email', description: 'Primary support email address', category: 'ui_settings', data_type: 'string', value: 'support@example.com', is_required: false },
    { key: 'contact_phone', display_name: 'Contact Phone', description: 'Contact phone number', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'business_hours', display_name: 'Business Hours', description: 'Support business hours', category: 'ui_settings', data_type: 'string', value: '9 AM - 5 PM EST', is_required: false },
    { key: 'help_center_url', display_name: 'Help Center URL', description: 'Link to help documentation', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    
    // Social Media
    { key: 'twitter_url', display_name: 'Twitter URL', description: 'Twitter/X profile URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'facebook_url', display_name: 'Facebook URL', description: 'Facebook page URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'linkedin_url', display_name: 'LinkedIn URL', description: 'LinkedIn company page URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'instagram_url', display_name: 'Instagram URL', description: 'Instagram profile URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'youtube_url', display_name: 'YouTube URL', description: 'YouTube channel URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    { key: 'discord_url', display_name: 'Discord URL', description: 'Discord community URL', category: 'forms_links', data_type: 'url', value: '', is_required: false },
    
    // Demo Video Settings
    { key: 'demo_video_url', display_name: 'Demo Video URL', description: 'YouTube URL for product demo video', category: 'ui_settings', data_type: 'url', value: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', is_required: false },
    { key: 'demo_video_title', display_name: 'Demo Video Title', description: 'Title for the demo video modal', category: 'ui_settings', data_type: 'string', value: 'Product Demo', is_required: false },
    
    // SEO & Analytics
    { key: 'meta_description', display_name: 'Meta Description', description: 'SEO meta description', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'meta_keywords', display_name: 'Meta Keywords', description: 'SEO meta keywords', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'google_analytics_id', display_name: 'Google Analytics ID', description: 'Google Analytics tracking ID', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'google_tag_manager_id', display_name: 'Google Tag Manager ID', description: 'Google Tag Manager container ID', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    { key: 'facebook_pixel_id', display_name: 'Facebook Pixel ID', description: 'Facebook Pixel tracking ID', category: 'ui_settings', data_type: 'string', value: '', is_required: false },
    
    // Maintenance & System
    { key: 'maintenance_mode', display_name: 'Maintenance Mode', description: 'Enable site-wide maintenance mode', category: 'system_health', data_type: 'boolean', value: 'false', is_required: false },
    { key: 'maintenance_message', display_name: 'Maintenance Message', description: 'Message to show during maintenance', category: 'system_health', data_type: 'string', value: 'Site is under maintenance. Please check back soon.', is_required: false },
    { key: 'api_rate_limit', display_name: 'API Rate Limit', description: 'API requests per minute limit', category: 'system_health', data_type: 'number', value: '100', is_required: false },
    { key: 'max_file_upload_size', display_name: 'Max Upload Size (MB)', description: 'Maximum file upload size in MB', category: 'system_health', data_type: 'number', value: '10', is_required: false },
    
    // Security
    { key: 'enable_recaptcha', display_name: 'Enable reCAPTCHA', description: 'Enable reCAPTCHA for forms', category: 'security', data_type: 'boolean', value: 'false', is_required: false },
    { key: 'recaptcha_site_key', display_name: 'reCAPTCHA Site Key', description: 'Google reCAPTCHA site key', category: 'security', data_type: 'string', value: '', is_required: false },
    { key: 'session_timeout', display_name: 'Session Timeout (minutes)', description: 'User session timeout in minutes', category: 'security', data_type: 'number', value: '1440', is_required: false },
    { key: 'password_min_length', display_name: 'Min Password Length', description: 'Minimum password length requirement', category: 'security', data_type: 'number', value: '8', is_required: false },
    
    // Image Generation Settings
    { key: 'default_image_quality', display_name: 'Default Image Quality', description: 'Default quality setting for image generation', category: 'image_generation', data_type: 'string', value: 'standard', is_required: false },
    { key: 'max_images_per_batch', display_name: 'Max Images Per Batch', description: 'Maximum number of images in a batch', category: 'image_generation', data_type: 'number', value: '4', is_required: false },
    { key: 'enable_batch_generation', display_name: 'Enable Batch Generation', description: 'Allow users to generate multiple images at once', category: 'image_generation', data_type: 'boolean', value: 'true', is_required: false },
    { key: 'image_storage_days', display_name: 'Image Storage Days', description: 'Number of days to store generated images', category: 'image_generation', data_type: 'number', value: '30', is_required: false },
  ];

  const handleConfigUpdate = async (key: string, value: string) => {
    setSaving(true);
    try {
      await updateConfiguration(key, value);
      toast({
        title: "Configuration Updated",
        description: "Setting has been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update configuration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleFeatureFlagUpdate = async (flagName: string, enabled: boolean) => {
    setSaving(true);
    try {
      await updateFeatureFlag(flagName, enabled);
      toast({
        title: "Feature Flag Updated",
        description: `${flagName} has been ${enabled ? 'enabled' : 'disabled'}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update feature flag. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleQuotaUpdate = async (provider: string, quotaType: string, limit: number) => {
    setSaving(true);
    try {
      await updateQuota(provider, quotaType, limit);
      toast({
        title: "Quota Updated",
        description: `${provider} ${quotaType} limit updated to ${limit}.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update quota. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddConfiguration = async () => {
    if (!newConfig.key || !newConfig.display_name) {
      toast({
        title: "Validation Error",
        description: "Key and Display Name are required.",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      await addConfiguration(newConfig);
      setNewConfigDialog(false);
      setNewConfig({
        key: '',
        value: '',
        display_name: '',
        description: '',
        category: 'forms_links',
        data_type: 'string',
        is_required: false,
        validation_rules: {}
      });
      toast({
        title: "Configuration Added",
        description: "New configuration has been created successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add configuration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfiguration = async (key: string) => {
    if (!confirm('Are you sure you want to delete this configuration?')) return;

    setSaving(true);
    try {
      await deleteConfiguration(key);
      toast({
        title: "Configuration Deleted",
        description: "Configuration has been removed successfully.",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete configuration. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const seedDefaultConfigurations = async () => {
    setSaving(true);
    try {
      const existingKeys = configurations?.map(config => config.key) || [];
      const configurationsToAdd = defaultConfigurations.filter(
        config => !existingKeys.includes(config.key)
      );

      for (const config of configurationsToAdd) {
        await addConfiguration({
          ...config,
          validation_rules: {}
        });
      }

      toast({
        title: "Default Configurations Added",
        description: `Added ${configurationsToAdd.length} default configurations.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add default configurations.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const getConfigsByCategory = (category: string) => {
    return configurations?.filter(config => config.category === category) || [];
  };

  const getConfigsByKeyPattern = (pattern: string) => {
    return configurations?.filter(config => config.key.includes(pattern)) || [];
  };

  const renderConfigInput = (config: any) => {
    const handleChange = (value: string) => {
      handleConfigUpdate(config.key, value);
    };

    switch (config.data_type) {
      case 'boolean':
        return (
          <div className="flex items-center space-x-2">
            <Switch
              checked={config.value === 'true'}
              onCheckedChange={(checked) => handleChange(checked.toString())}
              disabled={saving}
            />
            <Label>{config.display_name}</Label>
            {config.value === 'true' ? 
              <CheckCircle className="w-4 h-4 text-green-400" /> : 
              <AlertCircle className="w-4 h-4 text-gray-400" />
            }
          </div>
        );
      
      case 'number':
        return (
          <div className="space-y-2">
            <Label>{config.display_name}</Label>
            <Input
              type="number"
              value={config.value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
            />
          </div>
        );
      
      case 'json':
        return (
          <div className="space-y-2">
            <Label>{config.display_name}</Label>
            <Textarea
              value={config.value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
              placeholder={config.description}
              className="font-mono text-sm"
            />
          </div>
        );
      
      case 'url':
        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>{config.display_name}</Label>
              {config.value && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(config.value, '_blank')}
                  className="text-blue-400 hover:text-blue-300"
                >
                  <ExternalLink className="w-4 h-4" />
                </Button>
              )}
            </div>
            <Input
              type="url"
              value={config.value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
              placeholder="https://forms.google.com/..."
            />
          </div>
        );
      
      default:
        return (
          <div className="space-y-2">
            <Label>{config.display_name}</Label>
            <Input
              value={config.value}
              onChange={(e) => handleChange(e.target.value)}
              disabled={saving}
              placeholder={config.description}
            />
          </div>
        );
    }
  };

  if (configLoading || flagsLoading || quotasLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="bg-white/10 backdrop-blur-md border-white/20">
              <CardHeader>
                <div className="h-6 bg-white/10 rounded w-1/3"></div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="h-4 bg-white/10 rounded w-full"></div>
                  <div className="h-4 bg-white/10 rounded w-2/3"></div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Site Configuration</h2>
          <p className="text-gray-400">Comprehensive website management and controls</p>
        </div>
        <div className="flex items-center space-x-4">
          <Button
            onClick={seedDefaultConfigurations}
            variant="outline"
            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
            disabled={saving}
          >
            <Plus className="w-4 h-4 mr-2" />
            Seed Defaults
          </Button>
          <Dialog open={newConfigDialog} onOpenChange={setNewConfigDialog}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Add Configuration
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-gray-900 border-gray-700 max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-white">Add New Configuration</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Key</Label>
                    <Input
                      value={newConfig.key}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, key: e.target.value }))}
                      placeholder="config_key"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-white">Display Name</Label>
                    <Input
                      value={newConfig.display_name}
                      onChange={(e) => setNewConfig(prev => ({ ...prev, display_name: e.target.value }))}
                      placeholder="Display Name"
                      className="bg-gray-800 border-gray-600 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white">Value</Label>
                  <Input
                    value={newConfig.value}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, value: e.target.value }))}
                    placeholder="Configuration value"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-white">Description</Label>
                  <Textarea
                    value={newConfig.description}
                    onChange={(e) => setNewConfig(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Description of this configuration"
                    className="bg-gray-800 border-gray-600 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white">Category</Label>
                    <Select value={newConfig.category} onValueChange={(value: 'forms_links' | 'image_generation' | 'user_management' | 'system_health' | 'ui_settings' | 'security') => setNewConfig(prev => ({ ...prev, category: value }))}>
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="forms_links">Forms & Links</SelectItem>
                        <SelectItem value="image_generation">Image Generation</SelectItem>
                        <SelectItem value="user_management">User Management</SelectItem>
                        <SelectItem value="system_health">System Health</SelectItem>
                        <SelectItem value="ui_settings">UI Settings</SelectItem>
                        <SelectItem value="security">Security</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-white">Data Type</Label>
                    <Select value={newConfig.data_type} onValueChange={(value: 'string' | 'number' | 'boolean' | 'json' | 'url') => setNewConfig(prev => ({ ...prev, data_type: value }))}>
                      <SelectTrigger className="bg-gray-800 border-gray-600 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="string">String</SelectItem>
                        <SelectItem value="number">Number</SelectItem>
                        <SelectItem value="boolean">Boolean</SelectItem>
                        <SelectItem value="url">URL</SelectItem>
                        <SelectItem value="json">JSON</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    checked={newConfig.is_required}
                    onCheckedChange={(checked) => setNewConfig(prev => ({ ...prev, is_required: checked }))}
                  />
                  <Label className="text-white">Required Configuration</Label>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setNewConfigDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddConfiguration} disabled={saving}>
                    Add Configuration
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPreviewMode(!previewMode)}
              className={previewMode ? "text-green-400" : "text-gray-400"}
            >
              {previewMode ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </Button>
            <Badge className="bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Real-time Updates
            </Badge>
          </div>
        </div>
      </div>

      <Tabs defaultValue="google-forms" className="w-full">
        <TabsList className="grid w-full grid-cols-10 bg-white/5 backdrop-blur-md border-white/10">
          <TabsTrigger value="google-forms" className="flex items-center space-x-2">
            <FormInput className="w-4 h-4" />
            <span>Forms</span>
          </TabsTrigger>
          <TabsTrigger value="pricing" className="flex items-center space-x-2">
            <DollarSign className="w-4 h-4" />
            <span>Pricing</span>
          </TabsTrigger>
          <TabsTrigger value="content" className="flex items-center space-x-2">
            <Globe className="w-4 h-4" />
            <span>Content</span>
          </TabsTrigger>
          <TabsTrigger value="contact" className="flex items-center space-x-2">
            <Mail className="w-4 h-4" />
            <span>Contact</span>
          </TabsTrigger>
          <TabsTrigger value="social" className="flex items-center space-x-2">
            <Share2 className="w-4 h-4" />
            <span>Social</span>
          </TabsTrigger>
          <TabsTrigger value="seo" className="flex items-center space-x-2">
            <BarChart3 className="w-4 h-4" />
            <span>SEO</span>
          </TabsTrigger>
          <TabsTrigger value="maintenance" className="flex items-center space-x-2">
            <Wrench className="w-4 h-4" />
            <span>System</span>
          </TabsTrigger>
          <TabsTrigger value="security" className="flex items-center space-x-2">
            <Shield className="w-4 h-4" />
            <span>Security</span>
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center space-x-2">
            <Settings className="w-4 h-4" />
            <span>Features</span>
          </TabsTrigger>
          <TabsTrigger value="quotas" className="flex items-center space-x-2">
            <AlertTriangle className="w-4 h-4" />
            <span>Quotas</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="google-forms" className="space-y-6">
          <AdminSiteConfigBugReportForm />
          <AdminSiteConfigPricingForm />
          <AdminSiteConfigFeedbackForm />
          <AdminSiteConfigContactForm />
        </TabsContent>

        <TabsContent value="pricing" className="space-y-6">
          <AdminSiteConfigPricingSettings />
        </TabsContent>

        <TabsContent value="content" className="space-y-6">
          <AdminSiteConfigContentSettings />
        </TabsContent>

        <TabsContent value="contact" className="space-y-6">
          <AdminSiteConfigContactSettings />
        </TabsContent>

        <TabsContent value="social" className="space-y-6">
          <AdminSiteConfigSocialMediaSettings />
        </TabsContent>

        <TabsContent value="seo" className="space-y-6">
          <AdminSiteConfigSEOSettings />
        </TabsContent>

        <TabsContent value="maintenance" className="space-y-6">
          <AdminSiteConfigSystemSettings />
        </TabsContent>

        <TabsContent value="security" className="space-y-6">
          <AdminSiteConfigSecuritySettings />
        </TabsContent>

        <TabsContent value="features" className="space-y-6">
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <Settings className="w-5 h-5" />
                <span>Feature Flags</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {featureFlags?.map((flag) => (
                  <div key={flag.flag_name} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <Label className="text-white">{flag.display_name}</Label>
                        <p className="text-sm text-gray-400">{flag.description}</p>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs text-gray-500">Plans:</span>
                          {flag.target_plans.map((plan) => (
                            <Badge key={plan} variant="outline" className="text-xs">
                              {plan}
                            </Badge>
                          ))}
                        </div>
                      </div>
                      <Switch
                        checked={flag.is_enabled}
                        onCheckedChange={(checked) => handleFeatureFlagUpdate(flag.flag_name, checked)}
                        disabled={saving}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="quotas" className="space-y-6">
          <Card className="bg-white/10 backdrop-blur-md border-white/20">
            <CardHeader>
              <CardTitle className="text-white flex items-center space-x-2">
                <AlertTriangle className="w-5 h-5" />
                <span>API Quotas Management</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quotas?.map((quota) => (
                  <div key={`${quota.provider}-${quota.quota_type}`} className="p-4 rounded-lg bg-white/5 border border-white/10">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Badge variant="outline">{quota.provider}</Badge>
                        <Badge className={quota.is_active ? "bg-green-600/20 text-green-400" : "bg-red-600/20 text-red-400"}>
                          {quota.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-white capitalize">
                          {quota.quota_type.replace('_', ' ')}
                        </Label>
                        <Input
                          type="number"
                          value={quota.limit_value}
                          onChange={(e) => handleQuotaUpdate(quota.provider, quota.quota_type, parseInt(e.target.value))}
                          disabled={saving}
                          className="mt-1"
                        />
                      </div>
                      <div className="text-sm text-gray-400">
                        Current Usage: {quota.current_usage}/{quota.limit_value}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSiteConfiguration;
